import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Business as IceIcon,
} from '@mui/icons-material';
import {
  SlideUpTransition,
  useMinimizable,
  MinimizeButton,
  MinimizedBar,
} from '../../components/MinimizableDialog';
import { Devis, DevisCreate, DevisUpdate, DevisItemCreate, DevisStatus } from '../../types/devis';
import { Client } from '../../types/client';
import { Article } from '../../types';
import { ConditionPaiement } from '../../types/conditionPaiement';
import { ModePaiement } from '../../types/modePaiement';
import {
  devisAPI,
  clientsAPI,
  articlesAPI,
  conditionsPaiementAPI,
  modesPaiementAPI,
} from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import useFormDraft from '../../hooks/useFormDraft';
import { extractErrorMessage } from '../../utils/errorHandler';

interface LineItem {
  article_id: number | null | undefined;
  designation: string;
  quantite: number | null | string;
  prix_unitaire: number | null | string;
  total: number;
  article?: Article;
}

interface DevisFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  devisId?: number;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const DevisFormDialog: React.FC<DevisFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  devisId,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const isEdit = !!devisId;

  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [clients, setClients] = useState<Client[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [conditionsPaiement, setConditionsPaiement] = useState<ConditionPaiement[]>([]);
  const [modesPaiement, setModesPaiement] = useState<ModePaiement[]>([]);

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<ConditionPaiement | null>(null);
  const [selectedMode, setSelectedMode] = useState<ModePaiement | null>(null);

  const [formData, setFormData] = useState<{
    date: string;
    demande_numero: string;
    remise: number | null | string;
    delai_livraison: number | null | string;
    delai_devis_validation: number | null | string;
    status: string;
  }>({
    date: new Date().toISOString().split('T')[0],
    demande_numero: '',
    remise: null,
    delai_livraison: null,
    delai_devis_validation: null,
    status: DevisStatus.EN_COURS as string,
  });

  // « Envoyer au bureau d'études » with the validation (edit only: the projet is born at the validation)
  const [initialStatus, setInitialStatus] = useState<string>('');
  const [projetBe, setProjetBe] = useState<Devis['projet_be']>(null);
  const [projetCode, setProjetCode] = useState<string | null>(null);
  const [envoyerBe, setEnvoyerBe] = useState(false);
  const [beDessinateur, setBeDessinateur] = useState<number | ''>('');
  const [beInstructions, setBeInstructions] = useState('');
  const [dessinateurs, setDessinateurs] = useState<{ id: number; full_name: string }[]>([]);
  const [beDialog, setBeDialog] = useState(false);

  const [lineItems, setLineItems] = useState<LineItem[]>([
    { article_id: null, designation: '', quantite: null, prix_unitaire: null, total: 0 },
  ]);

  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Draft caching — only for new devis (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
    selectedClient: Client | null;
    selectedCondition: ConditionPaiement | null;
    selectedMode: ModePaiement | null;
    lineItems: LineItem[];
  }>('devis', !isEdit);

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      demande_numero: '',
      remise: null,
      delai_livraison: null,
      delai_devis_validation: null,
      status: DevisStatus.EN_COURS as string,
    });
    setSelectedClient(null);
    setSelectedCondition(null);
    setSelectedMode(null);
    setLineItems([{ article_id: null, designation: '', quantite: null, prix_unitaire: null, total: 0 }]);
  };

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const [clientsData, articlesData, conditionsData, modesData] = await Promise.all([
            clientsAPI.getAllForDropdown(),
            articlesAPI.getAllForDropdown(),
            conditionsPaiementAPI.getAll(),
            modesPaiementAPI.getAll(),
          ]);
          setClients(clientsData);
          setArticles(articlesData);
          setConditionsPaiement(conditionsData);
          setModesPaiement(modesData);

          if (isEdit && devisId) {
            devisAPI.dessinateurs().then(setDessinateurs).catch(() => setDessinateurs([]));
            const devis = await devisAPI.getById(devisId);
            populateForm(devis, clientsData, conditionsData, modesData);
          } else {
            const draft = loadDraft();
            if (draft) {
              setFormData(draft.formData);
              setSelectedClient(draft.selectedClient);
              setSelectedCondition(draft.selectedCondition);
              setSelectedMode(draft.selectedMode);
              setLineItems(draft.lineItems);
            } else {
              resetForm();
            }
          }
        } catch (err) {
          showError('Erreur lors du chargement des données');
          console.error(err);
        }
      };
      fetchData();
    }
  }, [open, devisId, isEdit]);

  const populateForm = (
    devis: Devis,
    clientsList: Client[],
    conditionsList: ConditionPaiement[],
    modesList: ModePaiement[]
  ) => {
    setFormData({
      date: devis.date,
      demande_numero: devis.demande_numero || '',
      remise: devis.remise,
      delai_livraison: devis.delai_livraison || null,
      delai_devis_validation: devis.delai_devis_validation || null,
      status: devis.status,
    });
    setInitialStatus(devis.status);
    setProjetBe(devis.projet_be || null);
    setProjetCode(devis.projet_code || null);
    setEnvoyerBe(false);  // already validated, not at the BE: sent only if ticked
    setBeDessinateur('');
    setBeInstructions('');

    const client = clientsList.find((c) => c.id === devis.client_id);
    setSelectedClient(client || null);

    const condition = conditionsList.find((c) => c.id === devis.condition_paiement_id);
    setSelectedCondition(condition || null);

    const mode = modesList.find((m) => m.id === devis.mode_paiement_id);
    setSelectedMode(mode || null);

    if (devis.items && devis.items.length > 0) {
      setLineItems(
        devis.items.map((item) => ({
          article_id: item.article_id,
          designation: item.designation,
          quantite: typeof item.quantite === 'string' ? parseFloat(item.quantite) : item.quantite,
          prix_unitaire: typeof item.prix_unitaire === 'string' ? parseFloat(item.prix_unitaire) : item.prix_unitaire,
          total: typeof item.total === 'string' ? parseFloat(item.total) : item.total,
        }))
      );
    }
  };

  const handleArticleChange = (index: number, article: Article | null) => {
    const newItems = [...lineItems];
    if (article) {
      newItems[index] = {
        ...newItems[index],
        article_id: article.id,
        designation: article.designation,
        article,
      };
    } else {
      newItems[index] = {
        ...newItems[index],
        article_id: null,
        designation: '',
        article: undefined,
      };
    }
    setLineItems(newItems);
  };

  const handleItemChange = (index: number, field: keyof LineItem, value: any) => {
    const newItems = [...lineItems];
    newItems[index] = { ...newItems[index], [field]: value };

    // Recalculate total when quantite or prix_unitaire changes
    if (field === 'quantite' || field === 'prix_unitaire') {
      const quantiteValue = newItems[index].quantite;
      const quantite = typeof quantiteValue === 'string'
        ? parseFloat(quantiteValue.replace(',', '.')) || 0
        : (quantiteValue ?? 0);

      const prixValue = newItems[index].prix_unitaire;
      const prix = typeof prixValue === 'string'
        ? parseFloat(prixValue.replace(',', '.')) || 0
        : (prixValue ?? 0);

      newItems[index].total = Math.round(quantite * prix * 100) / 100;
    }

    setLineItems(newItems);
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { article_id: null, designation: '', quantite: null, prix_unitaire: null, total: 0 },
    ]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateSubtotal = () => {
    const subtotal = lineItems.reduce((sum, item) => {
      const total = typeof item.total === 'string' ? parseFloat(item.total) || 0 : (item.total || 0);
      return sum + total;
    }, 0);
    return Math.round(subtotal * 100) / 100;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const remiseValue = formData.remise;
    const remise = typeof remiseValue === 'string'
      ? parseFloat(remiseValue.replace(',', '.')) || 0
      : (remiseValue ?? 0);
    const discount = Math.round(subtotal * (remise / 100) * 100) / 100;
    const total = subtotal - discount;
    return Math.round(total * 100) / 100;
  };

  /** be: the choice made in the « Envoyer au bureau d'études » dialog (undefined = not asked yet) */
  const handleSubmit = async (be?: { envoyer: boolean }) => {
    if (!selectedClient) {
      showError('Veuillez sélectionner un client');
      return;
    }

    const validItems = lineItems.filter((item) => {
      const quantite = typeof item.quantite === 'string'
        ? parseFloat(item.quantite.replace(',', '.'))
        : item.quantite;
      // Allow items with either article_id OR custom designation (or both)
      return item.designation && item.designation.trim() !== '' && quantite && quantite > 0;
    });

    if (validItems.length === 0) {
      showError('Veuillez ajouter au moins un article');
      return;
    }

    // Validating the devis: confirm first, with « Envoyer au bureau d'études » (ticked by default)
    if (be === undefined && isEdit && formData.status === DevisStatus.VALIDE && initialStatus !== DevisStatus.VALIDE && !projetBe) {
      setEnvoyerBe(true); setBeDessinateur(''); setBeInstructions('');
      setBeDialog(true);
      return;
    }

    try {
      setLoading(true);

      const items: DevisItemCreate[] = validItems.map((item) => {
        const quantite = typeof item.quantite === 'string'
          ? parseFloat(item.quantite.replace(',', '.')) || 0
          : (item.quantite ?? 0);
        const prixUnitaire = typeof item.prix_unitaire === 'string'
          ? parseFloat(item.prix_unitaire.replace(',', '.')) || 0
          : (item.prix_unitaire ?? 0);

        return {
          article_id: item.article_id ?? null,
          designation: item.designation,
          quantite: Math.round(quantite * 100) / 100,
          prix_unitaire: Math.round(prixUnitaire * 100) / 100,
          total: Math.round(item.total * 100) / 100,
        };
      });

      const remiseValue = formData.remise;
      const remise = typeof remiseValue === 'string'
        ? parseFloat(remiseValue.replace(',', '.')) || 0
        : (remiseValue ?? 0);

      if (isEdit && devisId) {
        const updateData: DevisUpdate = {
          date: formData.date,
          client_id: selectedClient.id,
          demande_numero: formData.demande_numero ? formData.demande_numero : null,
          remise: Math.round(remise * 100) / 100,
          condition_paiement_id: selectedCondition ? selectedCondition.id : null,
          mode_paiement_id: selectedMode ? selectedMode.id : null,
          delai_livraison: formData.delai_livraison ? Number(formData.delai_livraison) : null,
          delai_devis_validation: formData.delai_devis_validation ? Number(formData.delai_devis_validation) : null,
          status: formData.status,
          items,
          ...(formData.status === DevisStatus.VALIDE && !projetBe && be?.envoyer
            ? { envoyer_be: true, be_dessinateur_id: beDessinateur || null, be_instructions: beInstructions.trim() || null }
            : {}),
        };
        await devisAPI.update(devisId, updateData);
        showSuccess('Devis modifié avec succès');
      } else {
        const createData: DevisCreate = {
          date: formData.date,
          client_id: selectedClient.id,
          demande_numero: formData.demande_numero ? formData.demande_numero : null,
          remise: Math.round(remise * 100) / 100,
          condition_paiement_id: selectedCondition ? selectedCondition.id : null,
          mode_paiement_id: selectedMode ? selectedMode.id : null,
          delai_livraison: formData.delai_livraison ? Number(formData.delai_livraison) : null,
          delai_devis_validation: formData.delai_devis_validation ? Number(formData.delai_devis_validation) : null,
          items,
        };
        await devisAPI.create(createData);
        showSuccess('Devis créé avec succès');
      }

      clearDraft();
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // X / ANNULER = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    resetForm();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({ formData, selectedClient, selectedCondition, selectedMode, lineItems });
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={SlideUpTransition}
        PaperProps={{
          sx: { minHeight: '80vh' },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              {isEdit ? 'Modifier le Devis' : 'Nouveau Devis'}
            </Typography>
            <Box display="flex" alignItems="center">
              <MinimizeButton color="#4CAF50" onClick={minimize} />
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* General Information */}
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
            Informations Générales
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                size="small"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                value={selectedClient}
                onChange={(_, value) => setSelectedClient(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="Client" required size="small" />
                )}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="N° Demande"
                fullWidth
                size="small"
                value={formData.demande_numero}
                onChange={(e) => setFormData({ ...formData, demande_numero: e.target.value })}
              />
            </Grid>
          </Grid>

          {/* Client Contact Info */}
          {selectedClient && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                borderRadius: 1,
                border: '1px solid rgba(76, 175, 80, 0.3)',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1, fontWeight: 600 }}>
                Informations du Client
              </Typography>
              <Grid container spacing={2}>
          
                  <Grid item xs={12} sm={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <IceIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                      <Typography variant="body2">
                        <strong>ICE:</strong> 
                        
                        {selectedClient.ice ? selectedClient.ice : 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
             
           
                  <Grid item xs={12} sm={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                      <Typography variant="body2">
                        {selectedClient.name ? selectedClient.name : 'N/A'}
                        </Typography>
                    </Box>
                  </Grid>
            
       
           
                  <Grid item xs={12} sm={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                      <Typography variant="body2">
                        {selectedClient.address ? selectedClient.address : 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
      
                {!selectedClient.ice && !selectedClient.name && !selectedClient.phone && !selectedClient.mobile && !selectedClient.email && !selectedClient.address && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      Aucune information de contact disponible
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          <Grid container spacing={2} sx={{ mt: 0 }}>
            {isEdit && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.status}
                    label="Statut"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value={DevisStatus.EN_COURS}>En cours</MenuItem>
                    <MenuItem value={DevisStatus.VALIDE}>Validé</MenuItem>
                    <MenuItem value={DevisStatus.ANNULE}>Annulé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {isEdit && formData.status === DevisStatus.VALIDE && initialStatus === DevisStatus.VALIDE && (
            projetBe ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                {projetCode} est au bureau d'études · {projetBe.statut_label}
                {projetBe.dessinateur ? ` · Dessinateur : ${projetBe.dessinateur}` : ' · non assigné'}
              </Alert>
            ) : projetCode ? (
              <Alert severity="warning" sx={{ mt: 2 }}
                action={<Button color="inherit" size="small" onClick={() => {
                  setEnvoyerBe(true); setBeDessinateur(''); setBeInstructions(''); setBeDialog(true);
                }}>Envoyer au bureau d'études</Button>}>
                {projetCode} n'est pas encore au bureau d'études.
              </Alert>
            ) : null
          )}

          <Divider sx={{ my: 2 }} />

          {/* Articles Section */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Articles
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addLineItem}
              sx={{ borderColor: '#4CAF50', color: '#4CAF50' }}
            >
              Ajouter Article
            </Button>
          </Box>

          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.default' }}>
                  <TableCell sx={{ width: '35%' }}>Désignation</TableCell>
                  <TableCell sx={{ width: '15%' }} align="right">Quantité</TableCell>
                  <TableCell sx={{ width: '20%' }} align="right">Prix Unitaire</TableCell>
                  <TableCell sx={{ width: '20%' }} align="right">Total</TableCell>
                  <TableCell sx={{ width: '10%' }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={articles}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option;
                          }
                          return `${option.reference} - ${option.designation}`;
                        }}
                        value={
                          item.article_id
                            ? articles.find((a) => a.id === item.article_id) || null
                            : item.designation || ''
                        }
                        onChange={(_, value) => {
                          if (typeof value === 'string') {
                            // User typed and pressed Enter or selected free text
                            handleItemChange(index, 'designation', value);
                            handleItemChange(index, 'article_id', null);
                          } else if (value) {
                            // User selected an article from the list
                            handleArticleChange(index, value);
                          } else {
                            // User cleared the field
                            handleItemChange(index, 'designation', '');
                            handleItemChange(index, 'article_id', null);
                          }
                        }}
                        onBlur={(e) => {
                          // Capture the typed value on blur if no selection was made
                          const inputValue = (e.target as HTMLInputElement).value;
                          if (inputValue && !item.article_id) {
                            handleItemChange(index, 'designation', inputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            multiline
                            rows={3}
                            placeholder="Sélectionner ou saisir une désignation"
                            sx={{
                              '& .MuiInputBase-root': {
                                alignItems: 'flex-start',
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.reference} - {option.designation}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="text"
                        size="small"
                        value={item.quantite ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            handleItemChange(index, 'quantite', null);
                          } else if (/^\d*[.,]?\d*$/.test(value)) {
                            handleItemChange(index, 'quantite', value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && value !== '') {
                            const numValue = parseFloat(value.replace(',', '.'));
                            if (!isNaN(numValue)) {
                              handleItemChange(index, 'quantite', numValue);
                            } else {
                              handleItemChange(index, 'quantite', null);
                            }
                          }
                        }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="text"
                        size="small"
                        value={item.prix_unitaire ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            handleItemChange(index, 'prix_unitaire', null);
                          } else if (/^\d*[.,]?\d*$/.test(value)) {
                            handleItemChange(index, 'prix_unitaire', value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && value !== '') {
                            const numValue = parseFloat(value.replace(',', '.'));
                            if (!isNaN(numValue)) {
                              handleItemChange(index, 'prix_unitaire', numValue);
                            } else {
                              handleItemChange(index, 'prix_unitaire', null);
                            }
                          }
                        }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell align="right">
                      {item.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          {/* Payment Conditions & Totals */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
                Conditions de Paiement
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={conditionsPaiement}
                    getOptionLabel={(option) => option.nom}
                    value={selectedCondition}
                    onChange={(_, value) => setSelectedCondition(value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Condition de Paiement" size="small" />
                    )}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={modesPaiement}
                    getOptionLabel={(option) => option.nom}
                    value={selectedMode}
                    onChange={(_, value) => setSelectedMode(value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Mode de Paiement" size="small" />
                    )}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Délai de livraison (jours)"
                    type="number"
                    fullWidth
                    size="small"
                    value={formData.delai_livraison ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setFormData({ ...formData, delai_livraison: null });
                      } else {
                        const numValue = parseInt(value, 10);
                        if (!isNaN(numValue) && numValue >= 0) {
                          setFormData({ ...formData, delai_livraison: numValue });
                        }
                      }
                    }}
                    InputProps={{ inputProps: { min: 0 } }}
                    helperText="Nombre de jours après réception du bon de commande"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Validité du devis (jours)"
                    type="number"
                    fullWidth
                    size="small"
                    value={formData.delai_devis_validation ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setFormData({ ...formData, delai_devis_validation: null });
                      } else {
                        const numValue = parseInt(value, 10);
                        if (!isNaN(numValue) && numValue >= 0) {
                          setFormData({ ...formData, delai_devis_validation: numValue });
                        }
                      }
                    }}
                    InputProps={{ inputProps: { min: 0 } }}
                    helperText="Nombre de jours de validité du devis"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
                Totaux
              </Typography>
              <Box sx={{ backgroundColor: 'background.default', p: 2, borderRadius: 1 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Sous-total:</Typography>
                  <Typography variant="body2">
                    {calculateSubtotal().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">Remise (%):</Typography>
                  <TextField
                    type="text"
                    size="small"
                    value={formData.remise ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setFormData({ ...formData, remise: null });
                      } else if (/^\d*[.,]?\d*$/.test(value)) {
                        setFormData({ ...formData, remise: value });
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value && value !== '') {
                        const numValue = parseFloat(value.replace(',', '.'));
                        if (!isNaN(numValue)) {
                          // Clamp to 0-100 range
                          const clampedValue = Math.min(100, Math.max(0, numValue));
                          setFormData({ ...formData, remise: clampedValue });
                        } else {
                          setFormData({ ...formData, remise: null });
                        }
                      }
                    }}
                    sx={{ width: 80 }}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop="2px solid #4CAF50">
                  <Typography variant="subtitle1" fontWeight={600}>
                    Total:
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} color="#4CAF50">
                    {calculateTotal().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSubmit()}
            disabled={loading}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': { backgroundColor: '#388E3C' },
            }}
          >
            {loading ? 'Sauvegarde...' : isEdit ? 'Modifier' : 'Créer le Devis'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* « Modifier » on a devis being validated (or « Envoyer au bureau d'études » later) */}
      <Dialog open={beDialog} onClose={() => !loading && setBeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {initialStatus === DevisStatus.VALIDE ? `Envoyer ${projetCode || 'le projet'} au bureau d'études` : 'Valider le devis'}
        </DialogTitle>
        <DialogContent>
          {initialStatus !== DevisStatus.VALIDE && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Le devis passe à « Validé » et son projet est créé.
            </Typography>
          )}
          <FormControlLabel
            control={<Checkbox checked={envoyerBe} onChange={(e) => setEnvoyerBe(e.target.checked)} />}
            label="Envoyer le projet au bureau d'études" />
          {envoyerBe && (
            <>
              <TextField select fullWidth size="small" label="Dessinateur" value={beDessinateur} sx={{ mt: 1 }}
                onChange={(e) => setBeDessinateur(e.target.value === '' ? '' : Number(e.target.value))}
                helperText="Facultatif : sinon le premier dessinateur qui commence la conception la prend.">
                <MenuItem value=""><em>Non assigné</em></MenuItem>
                {dessinateurs.map((u) => <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>)}
              </TextField>
              <TextField fullWidth size="small" multiline minRows={3} label="Instructions pour le bureau d'études" sx={{ mt: 2 }}
                value={beInstructions} onChange={(e) => setBeInstructions(e.target.value)} helperText="Facultatif — imprimé sur la fiche A3" />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBeDialog(false)} disabled={loading}>Annuler</Button>
          <Button variant="contained" disabled={loading || (initialStatus === DevisStatus.VALIDE && !envoyerBe)}
            onClick={async () => { setBeDialog(false); await handleSubmit({ envoyer: envoyerBe }); }}
            sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#388E3C' } }}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#4CAF50"
        title={isEdit ? 'Modifier le Devis' : 'Nouveau Devis'}
        onRestore={restore}
        onClose={handleClose}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </>
  );
};

export default DevisFormDialog;
